// 🔹 React Core
import React, { FC, useMemo, useState } from 'react';

// 🔹 Drag & Drop Library
import { useDroppable } from '@dnd-kit/core';

// 🔹 UI Component Libraries
import { Dropdown, Modal, message } from 'antd';

// 🔹 Custom Hooks & Types
import { useProgramStore, type DaySlotType, type PlacedExercise } from './useProgramStore';

// 🔹 Assets
import icCopy from 'url:../../../../assets/images/program-menu/ic_copy.png';
import icEmpty from 'url:../../../../assets/images/program-menu/ic_empty.png';
import icPaste from 'url:../../../../assets/images/program-menu/ic_paste.png';
import icRemix from 'url:../../../../assets/images/program-menu/ic_remix.png';

interface ProgramDaySlotProps {
  store: ReturnType<typeof useProgramStore>;
  weekId: string;
  dayStr: string;
  slotType: DaySlotType;
  placedExercises: PlacedExercise[];
  recommendedCount: number;
  onOpenLibrary?: () => void;
}

export const ProgramDaySlot: FC<ProgramDaySlotProps> = ({
  store,
  weekId,
  dayStr,
  slotType,
  placedExercises,
  recommendedCount,
  onOpenLibrary,
}) => {
  const {
    draggedExercise,
    removePlacedExercise,
    updatePlacedExercise,
    copySlot,
    pasteSlot,
    emptySlot,
    clipboard,
    setRemixModalOpen,
  } = store;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTraineeViewOpen, setIsTraineeViewOpen] = useState(false);
  const [editingEx, setEditingEx] = useState<PlacedExercise | null>(null);
  const [repOrDurationType, setRepOrDurationType] = useState<number | null>(3);
  const [repOrDuration, setRepOrDuration] = useState<string | ''>('');

  const getMetricSummary = (ex: PlacedExercise) => {
    if (ex.metricType === 'duration') return `${ex.sets}×${ex.duration || '0s'}`;
    return `${ex.sets}×${ex.reps || 0}`;
  };

  const handleEditOpen = (ex: PlacedExercise) => {
    const supportedMetrics = ex.supportedMetrics || ['reps', 'duration'];
    const metricType = ex.metricType || (ex.duration ? 'duration' : 'reps');

    setEditingEx({
      ...ex,
      metricType,
      supportedMetrics,
      restBetweenSets: ex.restBetweenSets || '30s',
      restAfterExercise: ex.restAfterExercise || '30s',
    });
    setRepOrDurationType(
      supportedMetrics.length === 2 ? 3 : supportedMetrics.includes('duration') ? 2 : 1,
    );
    setRepOrDuration(metricType === 'duration' ? 'Duration' : 'Reps');
    setIsEditModalOpen(true);
  };

  const handleSave = () => {
    if (editingEx) {
      updatePlacedExercise(weekId, dayStr, slotType, editingEx.placedId, {
        sets: editingEx.sets,
        reps: editingEx.reps,
        duration: editingEx.duration,
        metricType: editingEx.metricType,
        restBetweenSets: editingEx.restBetweenSets,
        restAfterExercise: editingEx.restAfterExercise,
      });
    }
    setIsEditModalOpen(false);
  };

  const handleCopy = () => {
    if (placedExercises.length > 0) {
      copySlot(placedExercises, slotType);
      message.success('Exercises copied to clipboard');
    }
  };

  const handlePaste = () => {
    if (!clipboard) {
      message.info('Clipboard is empty');
      return;
    }
    if (pasteSlot(weekId, dayStr, slotType)) {
      message.success('Exercises pasted successfully');
    } else {
      message.error(`Cannot paste: Slot type mismatch (Expected ${clipboard.type})`);
    }
  };

  const handleEmpty = () => {
    emptySlot(weekId, dayStr, slotType);
    message.success('Slot cleared');
  };

  const isMatchingType = useMemo(() => {
    if (!draggedExercise) return false;
    return draggedExercise.type.some((t) => t.toLowerCase() === slotType.toLowerCase());
  }, [draggedExercise, slotType]);

  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${weekId}-${dayStr}-${slotType}`,
  });

  return (
    <>
      <div
        ref={setNodeRef}
        className={`program-day-slot ${
          isOver ? (isMatchingType ? 'match-over' : 'mismatch-over') : ''
        } ${placedExercises?.length > 0 ? 'solid' : 'dashed'}`}
      >
        <div className="slot-header">
          <span>
            {slotType} · Recommended: {recommendedCount}
          </span>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'copy',
                  label: 'Copy',
                  icon: <img src={icCopy} alt="copy icon" width={18} height={18} />,
                  disabled: placedExercises.length === 0,
                  onClick: handleCopy,
                },
                { type: 'divider' },
                {
                  key: 'paste',
                  label: 'Paste',
                  icon: <img src={icPaste} alt="paste icon" width={18} height={18} />,
                  disabled: !clipboard,
                  onClick: handlePaste,
                },
                { type: 'divider' },
                {
                  key: 'remix',
                  label: 'Remix',
                  icon: <img src={icRemix} alt="remix icon" width={18} height={18} />,
                  onClick: () => setRemixModalOpen(true),
                  disabled: true, // For now, we don't have api to call, so disabled for now.
                },
                { type: 'divider' },
                {
                  key: 'empty',
                  label: 'Empty',
                  icon: <img src={icEmpty} alt="empty icon" width={18} height={18} />,
                  disabled: !placedExercises.length,
                  onClick: handleEmpty,
                },
              ],
            }}
            trigger={['click']}
            placement="bottomRight"
            rootClassName="slot-options"
          >
            <button className="slot-options">⋮</button>
          </Dropdown>
        </div>

        {placedExercises.length === 0 ? (
          <div className="slot-empty" onClick={onOpenLibrary}>
            <span className="plus-icon">+</span>
            <p>Drop here</p>
          </div>
        ) : (
          <div className="slot-items">
            {placedExercises.map((ex) => (
              <div key={ex.placedId} className="placed-exercise">
                <div className="placed-thumbnail">
                  <img src={ex.thumbnail || 'https://via.placeholder.com/40'} alt={ex.name} />
                </div>
                <div className="text-ellipsis placed-info">
                  <h4 className="text-ellipsis" title={ex.name}>
                    {ex.name}
                  </h4>
                  <p className="text-ellipsis">
                    {getMetricSummary(ex)} · {ex.rest}
                  </p>
                </div>
                <div className="placed-meta">
                  <span className="impact-text">{ex.impact}</span>
                  <div className="placed-actions">
                    <button id="edit-btn" className="edit-btn" onClick={() => handleEditOpen(ex)}>
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.95545 3.68744L12.3128 6.04477M10.9554 2.68744C11.2681 2.37484 11.692 2.19922 12.1341 2.19922C12.5762 2.19922 13.0002 2.37484 13.3128 2.68744C13.6254 3.00004 13.801 3.42402 13.801 3.86611C13.801 4.30819 13.6254 4.73217 13.3128 5.04477L4.13411 14.2234H1.80078V11.8421L10.9554 2.68744Z"
                          stroke="#FFA100"
                          strokeWidth="1.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => removePlacedExercise(weekId, dayStr, slotType, ex.placedId)}
                    >
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="#E5E5E5"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        title="Edit Exercise"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        centered
        width={480}
        rootClassName="pg-edit-modal pg-edit-exercise"
      >
        {editingEx && (
          <div className="pg-modal-content">
            {isTraineeViewOpen ? (
              <div className="pg-trainee-view-mode">
                <div className="trainee-view-header">
                  <h3 className="mt-0">Trainee View Mode</h3>
                  <p>
                    This is a preview mode example. Your actual selected exercise and media will
                    appear here in the live trainee view.
                  </p>
                  <button
                    className="exit-trainee-view-btn"
                    onClick={() => setIsTraineeViewOpen(false)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                    Exist View as Trainee
                  </button>
                </div>

                <div className="phone-mockup">
                  <div className="phone-frame">
                    <div className="phone-screen">
                      <div className="phone-status-bar">
                        <span className="time">9:41</span>
                        <div className="status-icons">
                          <svg
                            width={20}
                            height={13}
                            viewBox="0 0 20 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M19.2 1.14623C19.2 0.513183 18.7224 0 18.1333 0H17.0667C16.4776 0 16 0.513183 16 1.14623V11.0802C16 11.7132 16.4776 12.2264 17.0667 12.2264H18.1333C18.7224 12.2264 19.2 11.7132 19.2 11.0802V1.14623ZM11.7659 2.44528H12.8326C13.4217 2.44528 13.8992 2.97078 13.8992 3.61902V11.0527C13.8992 11.7009 13.4217 12.2264 12.8326 12.2264H11.7659C11.1768 12.2264 10.6992 11.7009 10.6992 11.0527V3.61902C10.6992 2.97078 11.1768 2.44528 11.7659 2.44528ZM7.43411 5.09433H6.36745C5.77834 5.09433 5.30078 5.62652 5.30078 6.28301V11.0377C5.30078 11.6942 5.77834 12.2264 6.36745 12.2264H7.43411C8.02322 12.2264 8.50078 11.6942 8.50078 11.0377V6.28301C8.50078 5.62652 8.02322 5.09433 7.43411 5.09433ZM2.13333 7.53962H1.06667C0.477563 7.53962 0 8.06421 0 8.71132V11.0547C0 11.7018 0.477563 12.2264 1.06667 12.2264H2.13333C2.72244 12.2264 3.2 11.7018 3.2 11.0547V8.71132C3.2 8.06421 2.72244 7.53962 2.13333 7.53962Z"
                              fill="#04051B"
                            />
                          </svg>

                          <svg
                            width={18}
                            height={13}
                            viewBox="0 0 18 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.5713 2.46628C11.0584 2.46639 13.4504 3.38847 15.2529 5.04195C15.3887 5.1696 15.6056 5.16799 15.7393 5.03834L17.0368 3.77487C17.1045 3.70911 17.1422 3.62004 17.1417 3.52735C17.1411 3.43467 17.1023 3.34603 17.0338 3.28104C12.3028 -1.09368 4.83907 -1.09368 0.108056 3.28104C0.039524 3.34598 0.000639766 3.4346 7.82398e-06 3.52728C-0.000624118 3.61996 0.0370483 3.70906 0.104689 3.77487L1.40255 5.03834C1.53615 5.16819 1.75327 5.1698 1.88893 5.04195C3.69167 3.38836 6.08395 2.46628 8.5713 2.46628ZM8.56795 6.68656C9.92527 6.68647 11.2341 7.19821 12.2403 8.12234C12.3763 8.2535 12.5907 8.25065 12.7234 8.11593L14.0106 6.79663C14.0784 6.72742 14.1161 6.63355 14.1151 6.536C14.1141 6.43844 14.0746 6.34536 14.0054 6.27757C10.9416 3.38672 6.19688 3.38672 3.13305 6.27757C3.06384 6.34536 3.02435 6.43849 3.02345 6.53607C3.02254 6.63366 3.06028 6.72752 3.12822 6.79663L4.41513 8.11593C4.54778 8.25065 4.76215 8.2535 4.89823 8.12234C5.90368 7.19882 7.21152 6.68713 8.56795 6.68656ZM11.0924 9.48011C11.0943 9.58546 11.0572 9.68703 10.9899 9.76084L8.81327 12.2156C8.74946 12.2877 8.66247 12.3283 8.5717 12.3283C8.48093 12.3283 8.39394 12.2877 8.33013 12.2156L6.1531 9.76084C6.08585 9.68697 6.04886 9.58537 6.05085 9.48002C6.05284 9.37467 6.09365 9.27491 6.16364 9.20429C7.55374 7.8904 9.58966 7.8904 10.9798 9.20429C11.0497 9.27497 11.0904 9.37476 11.0924 9.48011Z"
                              fill="#04051B"
                            />
                          </svg>

                          <svg
                            width={28}
                            height={13}
                            viewBox="0 0 28 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              opacity="0.35"
                              x="0.5"
                              y="0.5"
                              width={24}
                              height={12}
                              rx="3.8"
                              stroke="#04051B"
                            />
                            <path
                              opacity="0.4"
                              d="M26 4.78113V8.8566C26.8047 8.51143 27.328 7.70847 27.328 6.81886C27.328 5.92926 26.8047 5.1263 26 4.78113Z"
                              fill="#04051B"
                            />
                            <rect x={2} y={2} width={21} height={9} rx="2.5" fill="#04051B" />
                          </svg>
                        </div>
                      </div>

                      <div className="phone-nav">
                        <button className="nav-back">
                          <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15 18L9 12L15 6"
                              stroke="#04051B"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <span className="nav-title">Move details</span>
                        <button className="nav-more">
                          <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                              stroke="#07092C"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                              stroke="#07092C"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                              stroke="#07092C"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="phone-content">
                        <div className="exercise-header-nav">
                          <button className="arrow-btn">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="19" y1="12" x2="5" y2="12" />
                              <polyline points="12 19 5 12 12 5" />
                            </svg>
                          </button>
                          <h4>{editingEx.name}</h4>
                          <button className="arrow-btn">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                          </button>
                        </div>

                        <div className="exercise-media">
                          <img src={editingEx.thumbnail} alt={editingEx.name} />
                          <button className="expand-btn">
                            <svg
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 8V6H18C17.7167 6 17.4793 5.904 17.288 5.712C17.0967 5.52 17.0007 5.28267 17 5C16.9993 4.71733 17.0953 4.48 17.288 4.288C17.4807 4.096 17.718 4 18 4H20C20.55 4 21.021 4.196 21.413 4.588C21.805 4.98 22.0007 5.45067 22 6V8C22 8.28333 21.904 8.521 21.712 8.713C21.52 8.905 21.2827 9.00067 21 9C20.7173 8.99933 20.48 8.90333 20.288 8.712C20.096 8.52067 20 8.28333 20 8ZM2 8V6C2 5.45 2.196 4.97933 2.588 4.588C2.98 4.19667 3.45067 4.00067 4 4H6C6.28333 4 6.521 4.096 6.713 4.288C6.905 4.48 7.00067 4.71733 7 5C6.99933 5.28267 6.90333 5.52033 6.712 5.713C6.52067 5.90567 6.28333 6.00133 6 6H4V8C4 8.28333 3.904 8.521 3.712 8.713C3.52 8.905 3.28267 9.00067 3 9C2.71733 8.99933 2.48 8.90333 2.288 8.712C2.096 8.52067 2 8.28333 2 8ZM20 20H18C17.7167 20 17.4793 19.904 17.288 19.712C17.0967 19.52 17.0007 19.2827 17 19C16.9993 18.7173 17.0953 18.48 17.288 18.288C17.4807 18.096 17.718 18 18 18H20V16C20 15.7167 20.096 15.4793 20.288 15.288C20.48 15.0967 20.7173 15.0007 21 15C21.2827 14.9993 21.5203 15.0953 21.713 15.288C21.9057 15.4807 22.0013 15.718 22 16V18C22 18.55 21.8043 19.021 21.413 19.413C21.0217 19.805 20.5507 20.0007 20 20ZM4 20C3.45 20 2.97933 19.8043 2.588 19.413C2.19667 19.0217 2.00067 18.5507 2 18V16C2 15.7167 2.096 15.4793 2.288 15.288C2.48 15.0967 2.71733 15.0007 3 15C3.28267 14.9993 3.52033 15.0953 3.713 15.288C3.90567 15.4807 4.00133 15.718 4 16V18H6C6.28333 18 6.521 18.096 6.713 18.288C6.905 18.48 7.00067 18.7173 7 19C6.99933 19.2827 6.90333 19.5203 6.712 19.713C6.52067 19.9057 6.28333 20.0013 6 20H4ZM6 14V10C6 9.45 6.196 8.97933 6.588 8.588C6.98 8.19667 7.45067 8.00067 8 8H16C16.55 8 17.021 8.196 17.413 8.588C17.805 8.98 18.0007 9.45067 18 10V14C18 14.55 17.8043 15.021 17.413 15.413C17.0217 15.805 16.5507 16.0007 16 16H8C7.45 16 6.97933 15.8043 6.588 15.413C6.19667 15.0217 6.00067 14.5507 6 14Z"
                                fill="white"
                              />
                            </svg>
                          </button>
                        </div>

                        <div className="exercise-content">
                          <div className="exercise-card-mini">
                            <img src={editingEx.thumbnail} alt={editingEx.name} />
                            <div className="card-mini-info">
                              <h5>{editingEx.name}</h5>
                              <p>Primary muscle group</p>
                            </div>
                          </div>
                          <div className="exercise-instructions">
                            <div className="instructions-header">
                              <h6>How to perform a {editingEx.name.toLowerCase()}</h6>
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="18 15 12 9 6 15" />
                              </svg>
                            </div>
                            <div className="instructions-steps">
                              <div className="step">
                                <span className="step-num">1</span>
                                <p>Stand with feet shoulder-width apart, knees slightly bent.</p>
                              </div>
                              <div className="step">
                                <span className="step-num">2</span>
                                <p>Hinge at the hips, keeping your back flat and chest up.</p>
                              </div>
                              <div className="step">
                                <span className="step-num">3</span>
                                <p>
                                  Hold the barbell or dumbbells with a pronated grip (palms down).
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="pg-modal-header-info">
                  <div className="pg-modal-ex-display">
                    <img src={editingEx.thumbnail} alt={editingEx.name} />
                    <h3>{editingEx.name}</h3>
                  </div>
                  <button
                    className="pg-view-trainee-btn"
                    onClick={() => setIsTraineeViewOpen(true)}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    View as Trainee
                  </button>
                </div>

                <hr className="modal-hr" />

                <div className="pg-modal-form">
                  <div className="pg-form-row">
                    <div className="pg-form-field">
                      <label>Sets</label>
                      <input
                        type="number"
                        value={editingEx.sets}
                        onChange={(e) =>
                          setEditingEx((prev) =>
                            prev ? { ...prev, sets: parseInt(e.target.value) || 0 } : null,
                          )
                        }
                      />
                    </div>
                    <div
                      className={`pg-form-field ${repOrDurationType === 3 ? `justify-content-between` : ``}`}
                    >
                      {repOrDurationType === 3 ? (
                        <>
                          <div className="resp-or-duration">
                            <button
                              className={`${repOrDuration === 'Reps' ? `active` : ``}`}
                              onClick={() => {
                                setRepOrDuration('Reps');
                                setEditingEx((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        metricType: 'reps',
                                        reps: prev.reps || 12,
                                        duration: '',
                                      }
                                    : null,
                                );
                              }}
                            >
                              Reps
                            </button>{' '}
                            |{' '}
                            <button
                              className={`${repOrDuration === 'Duration' ? `active` : ``}`}
                              onClick={() => {
                                setRepOrDuration('Duration');
                                setEditingEx((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        metricType: 'duration',
                                        reps: '',
                                        duration: prev.duration || '30s',
                                      }
                                    : null,
                                );
                              }}
                            >
                              Duration (sec)
                            </button>
                          </div>
                        </>
                      ) : repOrDurationType === 2 ? (
                        <label>Duration (sec)</label>
                      ) : (
                        <label>Reps</label>
                      )}

                      <input
                        type="number"
                        value={
                          editingEx.metricType === 'duration'
                            ? (editingEx.duration || '').replace(/s$/, '')
                            : editingEx.reps
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditingEx((prev) =>
                            prev
                              ? prev.metricType === 'duration'
                                ? {
                                    ...prev,
                                    reps: '',
                                    duration: value ? value.replace(/s$/i, '') + 's' : '',
                                  }
                                : { ...prev, reps: value, duration: '' }
                              : null,
                          );
                        }}
                      />
                    </div>
                  </div>
                  <hr className="modal-hr" />
                  <div className="pg-rest-selector-section">
                    <label>Rest Between Sets</label>
                    <div className="pg-rest-pills">
                      {['0s', '30s', '60s', '90s'].map((time) => (
                        <button
                          key={time}
                          className={`pg-rest-pill ${editingEx.restBetweenSets === time ? 'active' : ''}`}
                          onClick={() =>
                            setEditingEx((prev) =>
                              prev ? { ...prev, restBetweenSets: time } : null,
                            )
                          }
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                    <div className="pg-form-field">
                      <label>Custom (sec)</label>
                      <input
                        type="number"
                        placeholder="120"
                        value={
                          !['0s', '30s', '60s', '90s'].includes(editingEx.restBetweenSets || '')
                            ? editingEx.restBetweenSets?.replace('s', '')
                            : ''
                        }
                        onChange={(e) =>
                          setEditingEx((prev) =>
                            prev ? { ...prev, restBetweenSets: e.target.value + 's' } : null,
                          )
                        }
                      />
                    </div>
                  </div>
                  <hr className="modal-hr" />
                  <div className="pg-rest-selector-section">
                    <label>Rest After Exercise</label>
                    <div className="pg-rest-pills">
                      {['0s', '30s', '60s', '90s'].map((time) => (
                        <button
                          key={time}
                          className={`pg-rest-pill ${editingEx.restAfterExercise === time ? 'active' : ''}`}
                          onClick={() =>
                            setEditingEx((prev) =>
                              prev ? { ...prev, restAfterExercise: time } : null,
                            )
                          }
                        >
                          {time}
                        </button>
                      ))}
                    </div>

                    <div className="pg-form-field">
                      <label>Custom (sec)</label>
                      <input
                        type="number"
                        placeholder="120"
                        value={
                          !['0s', '30s', '60s', '90s'].includes(editingEx.restAfterExercise || '')
                            ? editingEx.restAfterExercise?.replace('s', '')
                            : ''
                        }
                        onChange={(e) =>
                          setEditingEx((prev) =>
                            prev ? { ...prev, restAfterExercise: e.target.value + 's' } : null,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <hr className="modal-hr" />
                <button className="pg-modal-save-btn" onClick={handleSave}>
                  Save
                </button>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
